import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ordersService } from '../../services/orders.service';
import { MessageService } from 'primeng/api';
import { EmsServicesService } from 'src/app/module/ems-master/services/ems-services.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

export type Role = 'ROLE_KAM' | 'ROLE_PROCUREMENT' | 'ROLE_PROCUREMENT_HEAD' | 'ROLE_VENDOR';

export type CustomerRateType = 'RATE_KNOWN' | 'NULL_PRICE';

export interface OrderViewModel {
  id: number;
  orderNo: string;
  orderNumber: string;
  lrNumber: string | null;
  customer: string;
  customerName: string;
  accountId: number;
  customerId: number;
  vendorId: number;
  orderType: string;
  vehiclePd: string;
  vehicleRequiredAt: string;
  tripStatus: string;
  route: string;
  source: string;
  destination: string;
  assignedKam: string;
  assignedKamId: number;
  assignedProcExec: string;
  assignedProcurementId: number;
  numberOfVehicle: number;
  vehicleTypeId: number;
  status: string;
  biddingStatus: string;
  biddingEnable: number;
  customerRateType?: CustomerRateType;
  isNullPrice?: boolean;
  profitAdjustedCustomerAmount:any;

  // Amounts
  customerRate?: number;
  customerAmount?: number;
  historicalRate?: number;
  finalSecondPrice?: number | null;
  finalProcCost?: number | null;
  procurementProvidedAmount?: number;
  profitAdjustedProcAmount?: number;   // adjusted proc rate (proc + 5%)
  confirmedAmount?: number;
  procHeadAmount?: number;

  // Spot - null price
  adjustedProcRate?: number;
  finalBdRate?: number | null;         // KAM enters this

  // Procurement / Proc Head shared
  procurementRate?: number | null;
  targetCeiling?: number;
  remark?: string;

  // Contract
  contractRate?: number;

  // Points
  orderPoints?: any[];
  orderStatus?: any[];

  // Raw
  sourceId?: number;
  destinationId?: number;
  routeId?: number;
}

@Component({
  selector: 'app-view-tms-order',
  templateUrl: './view-tms-order.component.html',
  styleUrls: ['./view-tms-order.component.css']
})
export class ViewTmsOrderComponent implements OnInit {
  theme = 'light';

  @Input() role: Role = 'ROLE_KAM';
  @Input() userName = '';
  order!: OrderViewModel;
  orderId: any;
  isLoading = false;
  actionLoading = false;

  // KAM — Final BD Rate input
  finalBdRateInput: number | null = null;

  // HEAD — proc head amount input
  procHeadAmountInput: number | null = null;
  selectedVendorId: number | null = null;
  vendorList: any[] = [];
  kamList:any[]=[];
  allRoles:any;
  procurementExecList:any[]=[]

  // ── Submit Rate dialog state ──
    showSubmitRateDialog = false;
    submitRateStep: 'confirm' | 'form' = 'confirm';
    inviteVendors = false;
    currentOrderForRate: any = null;
    rateForm!: FormGroup;
   
  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private orderService: ordersService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private emsServices: EmsServicesService
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    this.role = userDetails?.roleName || 'ROLE_KAM';
    this.userName = userDetails?.name || '';
  
    this.orderId = this.activeRoute.snapshot.params['id'];
    if (this.orderId) {
      this.getOrderDatabyId(this.orderId);
    }
  
    if (this.role === 'ROLE_PROCUREMENT_HEAD') {
      this.getVendorList();
    }
    this.getRoles();
  }

  getRoles(){
    this.emsServices.getRoles().subscribe((res:any)=>{
      this.allRoles = res?.responseObject;
      this.allRoles.forEach((el:any)=>{
        if(el.name === 'ROLE_KAM'){
          this.getKam(el.id)
        }
        if(el.name === 'ROLE_PROCUREMENT'){
          this.getProcurement(el.id)
        }
      })
    })
  }

  getKam(id:any){
    const postData = [
      {
        "key": "ROLE_ID_IN",
        "value": id.toString()
      }
    ]
    this.emsServices.getUsers(postData,0,100000,'id','DESC').subscribe((res:any)=>{
      this.kamList = res?.responseObject?.content;
    })
  }

  getProcurement(id:any){
    const postData = [
      {
        "key": "ROLE_ID_IN",
        "value": id.toString()
      }
    ]
    this.emsServices.getUsers(postData,0,100000,'id','DESC').subscribe((res:any)=>{
      this.procurementExecList = res?.responseObject?.content;
    })
  }
  
  getVendorList(): void {
    const filters = [{ key: 'ORDER_ID', value: this.orderId.toString() }];
    this.orderService.getVendorOrders(filters, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.vendorList = res?.responseObject?.content || [];
    });
  }

  getOrderDatabyId(orderId: any) {
    this.isLoading = true;
    this.orderService.getOrderById(orderId).subscribe((res: any) => {
      this.isLoading = false;
      const data = res?.responseObject;
      if (data) {
        this.mapResponseToOrder(data);
      }
    }, err => {
      this.isLoading = false;
    });
  }

  mapResponseToOrder(data: any): void {
    const customerRateType: CustomerRateType = data.isNullPrice ? 'NULL_PRICE' : 'RATE_KNOWN';

    const pickup = data.orderPoints?.find((p: any) => p.pointType === 'PICKUP');
    const drop   = data.orderPoints?.find((p: any) => p.pointType === 'DROP');
    const route  = (data.source && data.destination)
      ? `${data.source} - ${data.destination}`
      : (pickup && drop)
        ? `${pickup.address} - ${drop.address}`
        : '';

    this.order = {
      id:                        data.id,
      orderNo:                   data.orderNumber,
      orderNumber:               data.orderNumber,
      lrNumber:                  data.lrNumber || null,
      customer:                  data.customerName || '',
      customerName:              data.customerName || '',
      accountId:                 data.accountId,
      customerId:                data.customerId,
      vendorId:                  data.vendorId,
      orderType:                 data.orderType || 'spot',
      vehiclePd:                 data.vehicleRequiredAt || '',
      vehicleRequiredAt:         data.vehicleRequiredAt || '',
      tripStatus:                data.tripStatus || data.status || '',
      route,
      source:                    data.source || pickup?.address || '',
      destination:               data.destination || drop?.address || '',
      assignedKam:               data.assignedKam || data.assignedKamId || '',
      assignedKamId:             data.assignedKamId,
      assignedProcExec:          data.assignedProcurement || data.assignedProcurementId || '',
      assignedProcurementId:     data.assignedProcurementId,
      numberOfVehicle:           data.numberOfVehicle || 1,
      vehicleTypeId:             data.vehicleTypeId,
      status:                    data.status || '',
      biddingStatus:             data.biddingStatus || '',
      biddingEnable:             data.biddingEnable || 0,
      customerRateType,
      isNullPrice:               data.isNullPrice,
      customerRate:              data.customerAmount,
      customerAmount:            data.customerAmount,
      historicalRate:            data.historicalRate || null,
      finalSecondPrice:          data.finalSecondPrice || null,
      finalProcCost:             data.finalConfirmedAmount || null,
      procurementProvidedAmount: data.procurementProvidedAmount,
      profitAdjustedCustomerAmount: data.profitAdjustedCustomerAmount,
      profitAdjustedProcAmount:  data.profitAdjustedProcAmount,
      confirmedAmount:           data.confirmedAmount,
      procHeadAmount:            data.procHeadAmount,
      procurementRate:           data.procurementProvidedAmount || null,
      adjustedProcRate:          data.profitAdjustedProcAmount ?? undefined,
      finalBdRate:               data.customerAmount || null,
      targetCeiling:             data.targetCeiling || undefined,
      remark:                    data.remarks || '',
      contractRate:              data.contractRate || undefined,
      orderPoints:               data.orderPoints || [],
      orderStatus:               data.orderStatus || [],
      sourceId:                  data.sourceId,
      destinationId:             data.destinationId,
      routeId:                   data.routeId,
    };

    // Pre-fill input fields from existing data
    // For contract use confirmedAmount, for spot use customerAmount
    this.finalBdRateInput    = data.confirmedAmount || data.customerAmount || null;
    this.procHeadAmountInput = data.procHeadAmount || null;
  }

  // ─────────────────────────────────────────────
  // STATUS HELPERS
  // ─────────────────────────────────────────────

  statusLabel(): string {
    if (!this.order?.status) return '--';
    return this.order.status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  statusBadgeClass(): string {
    const s = this.order?.status?.toUpperCase();
    if (!s) return 'badge-grey';
    if (s.includes('CONFIRMED') || s.includes('APPROVED') || s.includes('ACCEPTED')) return 'badge-green';
    if (s.includes('PENDING') || s.includes('HEAD')) return 'badge-yellow';
    if (s.includes('REJECTED')) return 'badge-red';
    if (s.includes('CREATED')) return 'badge-purple-outline';
    return 'badge-grey';
  }

  // ─────────────────────────────────────────────
  // SECTION VISIBILITY
  // ─────────────────────────────────────────────

  showPricingSection(): boolean {
    return this.role !== 'ROLE_VENDOR';
  }

  pricingSectionTitle(): string {
    if (this.role === 'ROLE_KAM') return 'Pricing';
    if (this.role === 'ROLE_PROCUREMENT') return this.isContract() ? 'Pricing' : 'Pricing — Procurement';
    if (this.role === 'ROLE_PROCUREMENT_HEAD') return 'Pricing — Full View';
    return 'Pricing';
  }

  showMarginBadge(): boolean {
    return this.role === 'ROLE_PROCUREMENT_HEAD' && (this.isSpot());
  }

  isSpot(): boolean {
    return this.order?.orderType?.toLowerCase() === 'spot';
  }

  isContract(): boolean {
    return this.order?.orderType?.toLowerCase() === 'contract';
  }

  marginPercent(): string {
    if (!this.order) return '0.0';
    const sell = this.order.isNullPrice
      ? (this.finalBdRateInput || this.order.customerAmount || 0)
      : (this.order.customerAmount || 0);
    const cost = this.order.procurementProvidedAmount || 0;
    if (!sell) return '0.0';
    return (((sell - cost) / sell) * 100).toFixed(1);
  }

  marginBadgeClass(): string {
    return parseFloat(this.marginPercent()) >= 10 ? 'badge-green' : 'badge-red';
  }

  // ─────────────────────────────────────────────
  // BANNERS
  // ─────────────────────────────────────────────

  infoBannerText(): string | null {
    if (!this.order || this.isContract()) return null;
    if (!this.order.isNullPrice) {
      if (this.role === 'ROLE_KAM' || this.role === 'ROLE_PROCUREMENT') {
        return 'Customer rate is the final billing amount.';
      }
    }
    if (this.order.isNullPrice) {
      if (this.role === 'ROLE_KAM') {
        return 'Enter Final BD Rate. If less than adjusted proc rate, order goes to Head for approval.';
      }
      if (this.role === 'ROLE_PROCUREMENT') {
        return 'No customer rate. Quote freely — KAM sees +5%.';
      }
    }
    return null;
  }

  targetCeilingValue(): number | null {
    if (!this.order) return null;
    if ((this.role === 'ROLE_PROCUREMENT' || this.role === 'ROLE_PROCUREMENT_HEAD') && !this.order.isNullPrice && this.isSpot()) {
      return this.order.targetCeiling ?? null;
    }
    return null;
  }

  showApprovalRequiredBanner(): boolean {
    return this.role === 'ROLE_PROCUREMENT_HEAD' && this.order?.status === 'HEAD_PENDING';
  }

  getMargin(order: any): number {
    const procurement = Number(order?.procurementProvidedAmount || 0);
    const customer = Number(order?.customerAmount || 0);
  
    return  customer - procurement;
  }
  
  getMarginPercentage(order: any): number {
    const customer = Number(order?.customerAmount || 0);
  
    if (!customer) {
      return 0;
    }
  
    const margin = this.getMargin(order);
    return (margin / customer) * 100;
  }
  // ─────────────────────────────────────────────
  // EDITABILITY
  // ─────────────────────────────────────────────

  canEditLrNumber(): boolean {
    return this.role === 'ROLE_KAM';
  }

  canEditProcExec(): boolean {
    return this.role === 'ROLE_KAM' || this.role === 'ROLE_PROCUREMENT';
  }

  canEditGeneralDetails(): boolean {
    return this.role === 'ROLE_KAM';
  }

  // ─────────────────────────────────────────────
  // KAM — SUBMIT FINAL BD RATE
  // amount < adjustedProcRate → HEAD_PENDING
  // amount >= adjustedProcRate → ORDER_CONFIRMED
  // ─────────────────────────────────────────────

  submitFinalBdRate(): void {
    if (!this.finalBdRateInput) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter Final BD Rate' });
      return;
    }
  
    // For contract (isNullPrice false) compare against customerAmount
    // For spot null price compare against profitAdjustedProcAmount (+5%)
    const threshold = (!this.order.isNullPrice || this.isContract())
      ? (this.order.customerAmount || 0)
      : (this.order.adjustedProcRate || this.order.profitAdjustedProcAmount || 0);
  
    const isLess = this.finalBdRateInput < threshold;

    const payload: any = {
      id:              this.order.id,
      status:          isLess ? 'HEAD_PENDING' : 'ORDER_CONFIRMED',
      confirmedAmount: this.finalBdRateInput,
      customerAmount:  this.finalBdRateInput
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: isLess
            ? 'Order sent to Procurement Head for approval'
            : 'Order confirmed successfully'
        });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // HEAD — APPROVE with procHeadAmount
  // ─────────────────────────────────────────────

  submitHeadApproval(): void {
    if (!this.procHeadAmountInput) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter Head Approved Amount' });
      return;
    }
  
    if (!this.selectedVendorId) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please select a vendor' });
      return;
    }
  
    const payload: any = {
      id:             this.order.id,
      status:         'HEAD_APPROVED',
      procHeadAmount: this.procHeadAmountInput,
      vendorIds:      [this.selectedVendorId]
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Approved by Head' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // KAM — CONFIRM after HEAD_APPROVED
  // ─────────────────────────────────────────────

  confirmOrder(): void {
    const payload: any = {
      id:              this.order.id,
      status:          'ORDER_CONFIRMED',
      confirmedAmount: this.order.procHeadAmount || this.order.confirmedAmount
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Order confirmed' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // REJECT — any role at any point
  // ─────────────────────────────────────────────

  rejectOrder(): void {
    const payload: any = {
      id:     this.order.id,
      status: 'REJECTED'
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Rejected', detail: 'Order rejected' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FORMATTING & NAVIGATION
  // ─────────────────────────────────────────────

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || value === ('' as any)) return '';
    return '₹' + Number(value).toLocaleString('en-IN');
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  saveGeneralDetails(): void {
    console.log('Save general details');
  }

  getVendorInfo(){
    this.orderService.getVendorInfo([],0,100000).subscribe((res:any)=>{
      this.vendorList = res?.responseObject?.content
    })
  }

  openSubmitRate(item: any): void {
    this.buildRateForm()
    this.getVendorInfo()
    this.currentOrderForRate = item;
    this.submitRateStep = 'confirm';
    this.inviteVendors = false;
    this.rateForm.reset();
    this.rateForm.controls['rate'].setValue(this.order?.profitAdjustedCustomerAmount.toString())
    this.showSubmitRateDialog = true;
  }
  
  buildRateForm(): void {
      this.rateForm = this.fb.group({
        rate:    [null, [Validators.required, Validators.min(1)]],
        vendors: [null, Validators.required]
      });
  }

  closeSubmitRateDialog(): void {
    this.showSubmitRateDialog = false;
  }
  onYesInvite(): void {
    this.inviteVendors = true;
    this.submitRateStep = 'form';
  }
 
  onNoJustSubmit(): void {
    this.inviteVendors = false;
    this.submitRateStep = 'form';
  }
 
  onRateBack(): void {
    this.submitRateStep = 'confirm';
    this.rateForm.reset();
  }
 
  isRateInvalid(field: string): boolean {
    const ctrl = this.rateForm.get(field);
    return !!ctrl && ctrl.touched && ctrl.invalid;
  }
 
  toggleSelectAll(): void {
    const allIds = this.vendorList.map(v => v.id);
    const selected = this.rateForm.get('vendors')?.value || [];
  
    if (selected.length === this.vendorList.length) {
      // all selected → deselect all
      this.rateForm.get('vendors')?.setValue([]);
    } else {
      // none or some selected → select all
      this.rateForm.get('vendors')?.setValue(allIds);
    }
  }

  isAllSelected(): boolean {
    const selected = this.rateForm.get('vendors')?.value || [];
    return selected.length === this.vendorList.length;
  }
  
  isSomeSelected(): boolean {
    const selected = this.rateForm.get('vendors')?.value || [];
    return selected.length > 0 && selected.length < this.vendorList.length;
  }
  onSubmitRate(): void {
  if (this.rateForm.invalid) {
    this.rateForm.markAllAsTouched();
    return;
  }

  const formValue = this.rateForm.value;

  let payload: any;

  if (this.inviteVendors) {
    payload = {
      id:                       this.currentOrderForRate?.id,
      procurementProvidedAmount: formValue.rate,
      biddingEnable:            1,
      biddingStatus:            'PendingOnVendor',
      vendorIds:                formValue.vendors
    };
  } else {
    payload = {
      id:                       this.currentOrderForRate?.id,
      procurementProvidedAmount: formValue.rate,
      status:                   'QUOTE_SHARED',
      vendorId:                 formValue.vendors
    };
  }

  console.log('Submit Rate Payload:', payload);

  this.orderService.createUpdateOrder(payload).subscribe({
    next: (res: any) => {
      this.showSubmitRateDialog = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: res?.responseMessage || 'Rate submitted successfully'
      });
    },
    error: (err: any) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err?.error?.responseMessage || 'Something went wrong'
      });
    }
  });
}
}